import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { api } from '../lib/api';
import MagicBento from '../components/MagicBento';
import { RUNS_BENTO_CARDS, RUNS_BENTO_GLOW } from '../data/magicBentoPresets';

export function ExecutorRunsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get('/api/pentest-executor/runs?limit=50');
        if (!mounted) return;
        setItems(res.items || []);
      } catch (e) {
        if (!mounted) return;
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold text-foreground">Executor runs</h1>
        <p className="text-muted-foreground">
          Recent allowlisted executor scans (nmap via local host or AERTAX bridge).
        </p>
      </div>

      <section className="space-y-3" aria-labelledby="runs-bento-heading">
        <h2
          id="runs-bento-heading"
          className="font-heading text-lg font-semibold tracking-tight text-foreground"
        >
          Execution footprint
        </h2>
        <MagicBento
          cards={RUNS_BENTO_CARDS}
          glowColor={RUNS_BENTO_GLOW}
          enableSpotlight={false}
          particleCount={8}
        />
      </section>

      <Card>
        <CardHeader>
          <CardTitle>Recent runs</CardTitle>
        </CardHeader>
        <CardContent>
          {loading && <p className="text-sm text-muted-foreground">Loading runs…</p>}
          {error && (
            <p className="text-sm text-red-500">
              {error}
            </p>
          )}
          {!loading && !error && items.length === 0 && (
            <p className="text-sm text-muted-foreground">No executor runs recorded yet.</p>
          )}
          {!loading && !error && items.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-xs uppercase text-muted-foreground">
                    <th className="px-2 py-1 text-left">Time</th>
                    <th className="px-2 py-1 text-left">Run id</th>
                    <th className="px-2 py-1 text-left">Target(s)</th>
                    <th className="px-2 py-1 text-left">Backend</th>
                    <th className="px-2 py-1 text-right">Hosts</th>
                    <th className="px-2 py-1 text-right">Open ports</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((run) => {
                    const created = run.created_at ? new Date(run.created_at) : null;
                    const firstTarget = (run.target_cidrs && run.target_cidrs[0]) || '—';
                    const extraTargets =
                      run.target_cidrs && run.target_cidrs.length > 1
                        ? ` +${run.target_cidrs.length - 1} more`
                        : '';
                    return (
                      <tr key={run.run_id} className="border-b border-border/60 last:border-0">
                        <td className="px-2 py-1 whitespace-nowrap text-xs text-muted-foreground">
                          {created ? created.toLocaleString() : '—'}
                        </td>
                        <td className="px-2 py-1">
                          <Link
                            to={`/runs/${encodeURIComponent(run.run_id)}`}
                            className="font-mono text-xs text-primary hover:underline"
                          >
                            {run.run_id}
                          </Link>
                        </td>
                        <td className="px-2 py-1 text-xs">
                          {firstTarget}
                          <span className="text-muted-foreground">{extraTargets}</span>
                        </td>
                        <td className="px-2 py-1">
                          <Badge variant="outline" className="text-xs">
                            {run.backend || 'local'}
                          </Badge>
                        </td>
                        <td className="px-2 py-1 text-right font-mono text-xs">
                          {run.hosts_found ?? 0}
                        </td>
                        <td className="px-2 py-1 text-right font-mono text-xs">
                          {run.open_ports_total ?? 0}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

